import { motion } from "framer-motion";
import { Linkedin, GraduationCap, Award, Trophy, BadgeCheck } from "lucide-react";

const FALLBACK_PHOTOS = [
    "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=640&q=80",
    "https://images.unsplash.com/photo-1594824476967-48c8b964273f?auto=format&fit=crop&w=640&q=80",
    "https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=640&q=80",
    "https://images.unsplash.com/photo-1580281658626-ee379f3cce93?auto=format&fit=crop&w=640&q=80",
];

function initials(name = "") {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
}

/**
 * Premium public formateur card.
 * variant:
 *   - "horizontal" : hero card, photo left / text right (used on FormationDetail single trainer)
 *   - "compact"    : 3-column grid card (used when multiple trainers or on cursus listing)
 */
export default function FormateurPublicCard({ formateur, variant = "compact", index = 0, dark = false }) {
    const f = formateur || {};
    const photo = f.photo_url || FALLBACK_PHOTOS[index % FALLBACK_PHOTOS.length];
    const name = f.full_name || "Formateur";
    const title = f.professional_title || f.specialty || "";
    const specialties = f.specialties && f.specialties.length ? f.specialties : [];
    const bio = f.short_bio || "";
    const quals = f.qualifications || [];
    const years = f.years_experience || 0;
    const linkedin = f.linkedin_url || "";
    const sportsAssoc = f.sports_associations || [];
    const proAssoc = f.professional_associations || [];

    if (variant === "horizontal") {
        return (
            <motion.article
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className={`grid gap-8 overflow-hidden rounded-3xl border p-6 md:grid-cols-[auto_1fr] md:gap-12 md:p-10 ${
                    dark
                        ? "border-white/10 bg-white/[0.03] text-white"
                        : "border-brand-line bg-white shadow-premium-lg text-brand-ink"
                }`}
                data-testid={`formateur-card-horizontal-${index}`}
            >
                <div className="relative w-full md:w-64">
                    <div className="aspect-[3/4] w-full overflow-hidden rounded-2xl bg-brand-sand md:w-64">
                        <img
                            src={photo}
                            alt={name}
                            loading="lazy"
                            className="h-full w-full object-cover"
                        />
                    </div>
                    {!f.photo_url && (
                        <div className="absolute left-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-white font-display text-sm text-brand-ink">
                            {initials(name)}
                        </div>
                    )}
                </div>

                <div className="flex flex-col">
                    <h3 className={`font-display text-3xl leading-tight md:text-4xl ${dark ? "text-white" : "text-brand-ink"}`}>
                        {name}
                    </h3>
                    {title && (
                        <p className={`mt-2 font-display italic text-lg ${dark ? "text-white/55" : "text-brand-smoke"}`}>
                            {title}
                        </p>
                    )}

                    {specialties.length > 0 && (
                        <div className="mt-5 flex flex-wrap gap-2">
                            {specialties.map((s) => (
                                <span
                                    key={s}
                                    className="inline-flex items-center rounded-full bg-brand-orange/12 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-orange"
                                >
                                    {s}
                                </span>
                            ))}
                        </div>
                    )}

                    {bio && (
                        <p className={`mt-6 max-w-2xl text-sm leading-relaxed md:text-base ${dark ? "text-white/70" : "text-brand-ink/75"}`}>
                            {bio}
                        </p>
                    )}

                    {quals.length > 0 && (
                        <div className="mt-7">
                            <div className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${dark ? "text-brand-orange" : "text-brand-orange"}`}>
                                Qualifications
                            </div>
                            <ul className="mt-4 space-y-2 text-sm">
                                {quals.map((q, i) => (
                                    <li key={`${q.name}-${i}`} className="flex items-start gap-2">
                                        <GraduationCap className={`mt-0.5 h-4 w-4 flex-shrink-0 ${dark ? "text-brand-orange" : "text-brand-orange"}`} />
                                        <span className={dark ? "text-white/80" : "text-brand-ink/80"}>
                                            <strong>{q.name}</strong>
                                            {q.year ? <span className="ml-1.5 opacity-60">— {q.year}</span> : null}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {(sportsAssoc.length > 0 || proAssoc.length > 0) && (
                        <div className="mt-7 grid gap-4 sm:grid-cols-2" data-testid={`formateur-card-associations-${index}`}>
                            {sportsAssoc.length > 0 && (
                                <div>
                                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
                                        <Trophy className="h-3.5 w-3.5" />
                                        Associations sportives
                                    </div>
                                    <ul className="mt-3 space-y-1.5 text-sm">
                                        {sportsAssoc.map((a) => (
                                            <li key={a} className="flex items-start gap-2">
                                                <span className={`mt-1.5 h-1 w-1 flex-shrink-0 rounded-full ${dark ? "bg-white/40" : "bg-brand-ink/40"}`} />
                                                <span className={dark ? "text-white/80" : "text-brand-ink/80"}>{a}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {proAssoc.length > 0 && (
                                <div>
                                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
                                        <BadgeCheck className="h-3.5 w-3.5" />
                                        Associations professionnelles
                                    </div>
                                    <ul className="mt-3 space-y-1.5 text-sm">
                                        {proAssoc.map((a) => (
                                            <li key={a} className="flex items-start gap-2">
                                                <span className={`mt-1.5 h-1 w-1 flex-shrink-0 rounded-full ${dark ? "bg-white/40" : "bg-brand-ink/40"}`} />
                                                <span className={dark ? "text-white/80" : "text-brand-ink/80"}>{a}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        {years > 0 && (
                            <span className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
                                <Award className="h-3 w-3" /> {years} ans d'expérience
                            </span>
                        )}
                        {linkedin && (
                            <a
                                href={linkedin}
                                target="_blank"
                                rel="noreferrer"
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${
                                    dark
                                        ? "border-white/20 text-white/80 hover:border-white/60 hover:text-white"
                                        : "border-brand-ink/20 text-brand-ink hover:border-brand-ink"
                                }`}
                            >
                                <Linkedin className="h-3 w-3" /> Voir le profil
                            </a>
                        )}
                    </div>
                </div>
            </motion.article>
        );
    }

    // compact
    return (
        <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.7, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className={`group h-full overflow-hidden rounded-3xl border ${
                dark ? "border-white/10 bg-white/[0.03]" : "border-brand-line bg-white shadow-premium"
            }`}
            data-testid={`formateur-card-compact-${index}`}
        >
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-brand-sand">
                <img
                    src={photo}
                    alt={name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                />
                {!f.photo_url && (
                    <div className={`absolute left-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full font-display text-sm ${
                        dark ? "border border-white/60 bg-white text-brand-ink" : "border border-brand-ink/15 bg-white text-brand-ink"
                    }`}>
                        {initials(name)}
                    </div>
                )}
                {years > 0 && (
                    <div className="absolute bottom-4 left-4 rounded-full bg-brand-orange px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white">
                        {years} ans
                    </div>
                )}
            </div>
            <div className={`p-6 ${dark ? "text-white" : "text-brand-ink"}`}>
                <div className="font-display text-xl leading-tight md:text-2xl">{name}</div>
                {title && (
                    <div className={`mt-1.5 text-xs italic leading-snug ${dark ? "text-white/55" : "text-brand-smoke"}`}>
                        {title}
                    </div>
                )}
                {specialties.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                        {specialties.slice(0, 3).map((s) => (
                            <span
                                key={s}
                                className="inline-flex items-center rounded-full bg-brand-orange/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-orange"
                            >
                                {s}
                            </span>
                        ))}
                    </div>
                )}
                {bio && (
                    <p className={`mt-4 line-clamp-3 text-sm leading-relaxed ${dark ? "text-white/65" : "text-brand-ink/65"}`}>
                        {bio}
                    </p>
                )}
                {quals.length > 0 && (
                    <ul className="mt-5 space-y-1.5 text-xs">
                        {quals.slice(0, 2).map((q, i) => (
                            <li key={`${q.name}-${i}`} className="flex items-start gap-2">
                                <GraduationCap className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-brand-orange" />
                                <span className={dark ? "text-white/70" : "text-brand-ink/70"}>
                                    {q.name}
                                    {q.year ? <span className="ml-1 opacity-60">— {q.year}</span> : null}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
                {(sportsAssoc.length > 0 || proAssoc.length > 0) && (
                    <div className="mt-5 flex flex-wrap gap-1.5" data-testid={`formateur-card-compact-assoc-${index}`}>
                        {sportsAssoc.slice(0, 2).map((a) => (
                            <span
                                key={`s-${a}`}
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-medium ${
                                    dark ? "border border-white/15 bg-white/5 text-white/80" : "border border-brand-orange/30 bg-brand-orange/5 text-brand-ink/85"
                                }`}
                                title={a}
                            >
                                <Trophy className="h-3 w-3 text-brand-orange" />
                                <span className="max-w-[110px] truncate">{a}</span>
                            </span>
                        ))}
                        {proAssoc.slice(0, 2).map((a) => (
                            <span
                                key={`p-${a}`}
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-medium ${
                                    dark ? "border border-white/15 bg-white/5 text-white/80" : "border border-brand-ink/15 bg-white text-brand-ink/85"
                                }`}
                                title={a}
                            >
                                <BadgeCheck className="h-3 w-3 text-brand-orange" />
                                <span className="max-w-[110px] truncate">{a}</span>
                            </span>
                        ))}
                        {sportsAssoc.length + proAssoc.length > 4 && (
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${dark ? "text-white/60" : "text-brand-smoke"}`}>
                                +{sportsAssoc.length + proAssoc.length - 4}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </motion.article>
    );
}
